'use client';

import { useTranslations } from 'next-intl';
import type { Table } from '@/lib/table-features';
import { Loader2, Trash2, CheckCircle, XCircle } from 'lucide-react';

import { useHashDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import type { BulkPayload } from '@/types/bulk-action';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { CONTAINER_TYPES_HASH } from '../constants';
import {
  useBulkDeleteContainerTypesMutation,
  useBulkChangeStatusContainerTypesMutation
} from '../services/container-types.mutations';
import type { ContainerType } from '../types';

interface ContainerTypesTableActionBarProps {
  table: Table<ContainerType>;
  getBulkPayload: () => BulkPayload;
  onComplete: () => void;
  isAllPagesSelected?: boolean;
  totalCount?: number;
}

export function ContainerTypesTableActionBar({
  table,
  getBulkPayload,
  onComplete,
  isAllPagesSelected,
  totalCount
}: ContainerTypesTableActionBarProps) {
  const t = useTranslations('containerTypes');
  const { open: bulkOpen, onOpenChange: toggleBulk } = useHashDialog(
    CONTAINER_TYPES_HASH.bulkDelete
  );

  const bulkDeleteMutation = useBulkDeleteContainerTypesMutation();
  const bulkChangeStatusMutation = useBulkChangeStatusContainerTypesMutation();

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const displayCount =
    isAllPagesSelected && totalCount ? totalCount : selectedRows.length;

  async function handleBulkDelete() {
    const payload = getBulkPayload();
    try {
      if (payload.mode === 'all') {
        await bulkDeleteMutation.mutateAsync({
          all: true,
          filters: payload.filters
        });
      } else {
        await bulkDeleteMutation.mutateAsync({ ids: payload.ids });
      }
      onComplete();
    } catch {
      // handled by global MutationCache
    }
    toggleBulk(false);
  }

  async function handleBulkChangeStatus(status: 'active' | 'inactive') {
    const payload = getBulkPayload();
    try {
      if (payload.mode === 'all') {
        await bulkChangeStatusMutation.mutateAsync({
          all: true,
          filters: payload.filters,
          status
        });
      } else {
        await bulkChangeStatusMutation.mutateAsync({
          ids: payload.ids,
          status
        });
      }
      onComplete();
    } catch {
      // handled by global MutationCache
    }
  }

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('selectedCount', { count: displayCount })}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => toggleBulk(true)}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('bulkDelete')}
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => handleBulkChangeStatus('active')}
            disabled={bulkChangeStatusMutation.isPending}
          >
            {bulkChangeStatusMutation.isPending ? (
              <Loader2 className='me-1.5 h-3.5 w-3.5 animate-spin' />
            ) : (
              <CheckCircle className='me-1.5 h-3.5 w-3.5' />
            )}
            {t('bulkActivate')}
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => handleBulkChangeStatus('inactive')}
            disabled={bulkChangeStatusMutation.isPending}
          >
            {bulkChangeStatusMutation.isPending ? (
              <Loader2 className='me-1.5 h-3.5 w-3.5 animate-spin' />
            ) : (
              <XCircle className='me-1.5 h-3.5 w-3.5' />
            )}
            {t('bulkDeactivate')}
          </Button>
        </div>
      </div>

      {/* Bulk delete confirmation */}
      <AlertDialog open={bulkOpen} onOpenChange={toggleBulk}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulkDeleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkDeleteConfirm', { count: displayCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {bulkDeleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
